import { cn } from '@optools/components/ui/utils';
import { EditorMilestone } from '@optools/services/operator-gateway/milestones/types';
import { MilestoneType } from '@optools/services/operator-gateway/types/generated/graphql';
import { MilestoneSliderThumb } from '@optools/tools/milestones/components/milestone-slider-thumb';
import { formatSecondsHHMMSS } from '@optools/utils/format';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-range';

import { useMilestoneEditor } from '../context/milestone-editor-context';
import { AdjustMilestoneDialog } from './adjust-milestone-dialog';
import { MilestoneTrimEditor } from './milestone-trim-editor';
import { MilestonesRange } from './milestones-range';

export interface MilestonesSliderProps {
  duration: number;
  mediaId: string;
  milestones: EditorMilestone[];
  programDateTime: string;
}

export function MilestonesSlider({
  duration,
  mediaId,
  milestones,
  programDateTime,
}: MilestonesSliderProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isAdjustOpen, setIsAdjustOpen] = useState<boolean>(false);
  const [range, setRange] = useState<number[]>([0, Math.ceil(duration)]);
  const [requestedTime, setRequestedTime] = useState<number | undefined>();
  const [values, setValues] = useState<number[]>([]);

  const { gridRef, seekTo, trimMode } = useMilestoneEditor();

  const times = useMemo(() => {
    return milestones.reduce<number[]>((acc, milestone) => {
      if (typeof milestone.videoTime === 'number') {
        acc.push(milestone.videoTime);
      }
      return acc;
    }, []);
  }, [milestones]);

  useEffect(() => {
    setValues(times);
  }, [times]);

  const [milestoneSliderMin, milestoneSliderMax] = range;
  const offset = times.filter((time) => time < milestoneSliderMin).length;
  const milestoneSliderStep = Math.ceil(100 / (milestoneSliderMax - milestoneSliderMin));
  const valuesInRange = values.filter(
    (time) => time >= milestoneSliderMin && time <= milestoneSliderMax,
  );

  const activeMilestone = typeof activeIndex === 'number' ? milestones[offset + activeIndex] : null;

  const onMilestoneSliderChange = (newValues: number[]) => {
    if (typeof activeIndex !== 'number') return;

    setValues(newValues);
    seekTo(newValues[activeIndex]);
  };

  const onSliderChangeCommitted = (newValues: number[]) => {
    if (typeof activeIndex !== 'number') return;

    const newTime = newValues[activeIndex];

    setRequestedTime(newTime);
    setIsAdjustOpen(true);
  };

  const renderMilestoneTooltip = (value: number, index: number, isDragged: boolean) => {
    const milestone = milestones[offset + index];
    if (!milestone) return null;

    const milestoneTime = milestone?.videoTime ?? 0;
    const currentTime = isDragged ? value : milestoneTime;
    return (
      <div className="flex flex-col items-center">
        <p
          className={cn(
            'text-sm whitespace-nowrap group-hover:flex',
            isDragged ? 'flex' : 'hidden',
          )}>
          {formatSecondsHHMMSS(currentTime)}
        </p>
        <p className="text-sm whitespace-nowrap">{offset + index}</p>
      </div>
    );
  };

  const onHoverThumb = (_value: number, thumbIndex: number) => {
    if (activeMilestone) return;

    const index = offset + thumbIndex;

    gridRef?.current?.scrollToIndexes(index);

    const to = milestones?.[index]?.videoTime;
    if (typeof to === 'number') {
      seekTo(to);
    }
  };

  const handleAdjustClose = () => {
    setValues(times);

    if (activeMilestone?.videoTime) {
      seekTo(activeMilestone.videoTime);
    }

    setActiveIndex(null);
    setRequestedTime(undefined);
    setIsAdjustOpen(false);
  };

  const streamStartTime = useMemo(
    () =>
      milestones.find((milestone) => milestone.milestoneType === MilestoneType.STREAM_START)
        ?.videoTime,
    [milestones],
  );

  const streamStopTime = useMemo(
    () =>
      milestones.find((milestone) => milestone.milestoneType === MilestoneType.STREAM_STOP)
        ?.videoTime,
    [milestones],
  );

  return (
    <div className={cn('flex flex-col', { 'mt-6 px-1': trimMode, 'mt-8 px-12': !trimMode })}>
      {!trimMode && <MilestonesRange duration={duration} values={range} onChange={setRange} />}

      <MilestoneTrimEditor
        max={milestoneSliderMax}
        mediaId={mediaId}
        min={milestoneSliderMin}
        programDateTime={programDateTime}
        step={milestoneSliderStep}
        streamStartTime={streamStartTime}
        streamStopTime={streamStopTime}>
        <Range
          allowOverlap
          max={milestoneSliderMax}
          min={milestoneSliderMin}
          renderThumb={({ index, isDragged, props, value }) => (
            <MilestoneSliderThumb
              key={props.key}
              alwaysShowTooltip={true}
              index={index}
              isDragged={isDragged}
              props={props}
              renderTooltipLabel={renderMilestoneTooltip}
              value={value}
              onDragThumb={(_, index) => setActiveIndex(index)}
              onHoverThumb={onHoverThumb}
            />
          )}
          renderTrack={({ children, props }) => {
            return (
              <div
                ref={props.ref}
                className="relative h-1 w-full rounded-full bg-muted"
                data-testid="milestone-range-slider">
                {children}
              </div>
            );
          }}
          step={milestoneSliderStep}
          values={valuesInRange}
          onChange={onMilestoneSliderChange}
          onFinalChange={onSliderChangeCommitted}
        />
      </MilestoneTrimEditor>

      <AdjustMilestoneDialog
        activeMilestone={activeMilestone}
        mediaId={mediaId}
        open={isAdjustOpen}
        programDateTime={programDateTime}
        requestedTime={requestedTime}
        onOpenChange={handleAdjustClose}
      />
    </div>
  );
}
